/**
 * CopyToClipboardButton - Reusable button component for copying text to clipboard
 *
 * Shows "Copied!" feedback for 1 second after successful copy operation.
 * Handles clipboard operations with fallback for older browsers.
 *
 * Usage:
 * ```tsx
 * <CopyToClipboardButton
 *   textToCopy="Hello, world!"
 *   size="sm"
 *   variant="outline"
 * >
 *   Copy Text
 * </CopyToClipboardButton>
 * ```
 */

import { Button, type ButtonProps, Text } from '@chakra-ui/react';
import { Copy } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface CopyToClipboardButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** Text to copy to clipboard, or a function that returns the text */
  textToCopy: string | (() => string | Promise<string>);

  /** Button content (defaults to "Copy" if not provided) */
  children?: ReactNode;

  /** Show icon (defaults to true) */
  showIcon?: boolean;

  /** Duration in milliseconds to show "Copied!" message (defaults to 1000) */
  copiedDuration?: number;
}

/**
 * Helper function to copy text to clipboard with fallback
 */
const copyToClipboard = async (textToCopy: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(textToCopy);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = textToCopy;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (fallbackError) {
      console.error('Fallback copy also failed:', fallbackError);
      throw fallbackError;
    }
    document.body.removeChild(textarea);
  }
};

export const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = ({
  textToCopy,
  children,
  showIcon = true,
  copiedDuration = 1000,
  ...buttonProps
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const originalText = children ?? 'Copy';

  // Measure the button width with original text and set as min-width
  useEffect(() => {
    if (buttonRef.current && minWidth === undefined && !isCopied) {
      // Use requestAnimationFrame to ensure the button is fully laid out
      requestAnimationFrame(() => {
        if (buttonRef.current) {
          const width = buttonRef.current.getBoundingClientRect().width;
          if (width > 0) {
            setMinWidth(width);
          }
        }
      });
    }
  }, [minWidth, isCopied]);

  const handleClick = useCallback(async () => {
    try {
      // Get text to copy (handle both string and function cases)
      const text = typeof textToCopy === 'function' ? await textToCopy() : textToCopy;

      await copyToClipboard(text);

      // Show "Copied!" feedback
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, copiedDuration);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Optionally show error feedback here if needed
    }
  }, [textToCopy, copiedDuration]);

  const displayText = isCopied ? 'Copied!' : originalText;

  return (
    <Button
      {...buttonProps}
      ref={buttonRef}
      onClick={handleClick}
      minWidth={minWidth ? `${minWidth}px` : undefined}
    >
      {showIcon && <Copy size={14} />}
      <Text ml={showIcon ? 1 : 0}>{displayText}</Text>
    </Button>
  );
};
